import {useState, useEffect, useRef, FormEvent, ChangeEvent} from 'react';
import {useParams, Link, useNavigate} from 'react-router-dom';
import {useAuth} from '../contexts/AuthContext';
import {useNotification} from '../components/Notification';
import {
    PlusIcon, TrashIcon, CopyIcon, FolderIcon,
    LogoutIcon, SaveIcon, ListIcon, ImageIcon
} from '../components/Icons';
import {
    createProduct, updateProduct, getProduct,
    productExists, slugify, deleteProductFields
} from '../firebase';
import type {
    ProductConfig, CTAActionType, CTAConfig, ColorVariant, CustomVariant, VariantOverride, VariantSpec,
    Language, LangValues
} from '../types';
import {LANGUAGES, emptyLangValues, toLangValues, fromLangValues} from '../types';
import {t} from '../locales';
import {LangInput, LangTabs, isTranslationMissing} from '../components/LangFields';

// Local form types - çevrilebilir alanlar her dil için düz string tutar
interface FormColorVariant {
    id: string;
    name: LangValues;
    color: string;
    pattern?: string;  // Doku görseli (base64) - varsa color yerine kullanılır
    specs?: FormVariantSpec[];
    isDisabled?: boolean;
    isDefault?: boolean;
}

interface FormCustomVariant {
    id: string;
    name: LangValues;
    specs?: FormVariantSpec[];
    isDisabled?: boolean;
    isDefault?: boolean;
}

interface FormVariantSpec {
    label: LangValues;
    value: LangValues;
}

interface FormFeatureItem {
    text: LangValues;
}

const DEFAULT_CTA_LABEL: LangValues = {tr: 'Teklif Al', en: 'Get Quote', fr: 'Demander un devis', de: 'Angebot anfordern'};

// Varsayılan mesaj şablonu: ürün sayfasının kullandığı locale metinleri
const defaultMessageTemplate = (): LangValues =>
    Object.fromEntries(LANGUAGES.map(lang => [lang, t('cta.defaultMessage', lang)])) as LangValues;

const emptySpec = (): FormVariantSpec => ({label: emptyLangValues(), value: emptyLangValues()});
const emptyColorVariant = (): FormColorVariant => ({id: '', name: emptyLangValues(), color: '#6B7280'});

const toFormSpecs = (specs?: VariantSpec[]): FormVariantSpec[] =>
    (specs || []).map(s => ({label: toLangValues(s.label), value: toLangValues(s.value)}));

// Türkçe etiketi ve değeri dolu olan özellikler kaydedilir
const fromFormSpecs = (specs?: FormVariantSpec[]): VariantSpec[] =>
    (specs || [])
        .filter(s => s.label.tr.trim() && s.value.tr.trim())
        .map(s => ({label: fromLangValues(s.label), value: fromLangValues(s.value)}));

// Renk bazlı override için form state
interface FormVariantOverride {
    enabled: boolean;  // Override aktif mi?
    count?: number;
    extension?: string;
    wrapAround?: boolean;
    galleryMode?: boolean;  // true = statik fotoğraf galerisi, false/undefined = 360° döndürme
}

const CTA_ACTIONS: { id: CTAActionType; name: string; placeholder: string }[] = [
    {id: 'link', name: 'Sayfaya Yönlendir', placeholder: 'https://example.com/sayfa'},
    {id: 'email', name: 'E-posta Gönder', placeholder: 'info@example.com'},
    {id: 'whatsapp', name: 'WhatsApp Mesajı', placeholder: '+905551234567'}
];

interface Props {
    editMode?: boolean;
}

export default function AddProductPage({editMode = false}: Props) {
    const {productId: editId} = useParams<{ productId?: string }>();
    const navigate = useNavigate();
    const {signOut} = useAuth();
    const {showNotification, NotificationComponent} = useNotification();

    // Form state - Basic info (tüm diller)
    const [productId, setProductId] = useState('');
    const [name, setName] = useState<LangValues>(emptyLangValues);
    const [description, setDescription] = useState<LangValues>(emptyLangValues);
    const [badge, setBadge] = useState<LangValues>(emptyLangValues);

    // Formda düzenlenen içerik dili
    const [editLang, setEditLang] = useState<Language>('tr');

    // Variants
    const [colorVariants, setColorVariants] = useState<FormColorVariant[]>(() => [emptyColorVariant()]);
    const [customVariants, setCustomVariants] = useState<FormCustomVariant[]>([]);

    // Specs
    const [specsTitle, setSpecsTitle] = useState<LangValues>(emptyLangValues);
    const [specs, setSpecs] = useState<FormVariantSpec[]>([]);

    // Images
    const [imagesPath, setImagesPath] = useState('');
    const [imagesCount, setImagesCount] = useState<number | ''>(24);
    const [imagesExt, setImagesExt] = useState('jpg');
    const [wrapAround, setWrapAround] = useState(true); // 360° döndürme
    const [galleryMode, setGalleryMode] = useState(false); // Galeri modu

    // Renk bazlı override'lar (key: "colorId")
    const [variantOverrides, setVariantOverrides] = useState<Record<string, FormVariantOverride>>({});

    // Features
    const [features, setFeatures] = useState<FormFeatureItem[]>([]);

    // CTA Button state
    const [ctaEnabled, setCtaEnabled] = useState(true);
    const [ctaLabel, setCtaLabel] = useState<LangValues>(() => ({...DEFAULT_CTA_LABEL}));
    const [ctaAction, setCtaAction] = useState<CTAActionType>('email');
    const [ctaValue, setCtaValue] = useState('');
    const [ctaEmailSubject, setCtaEmailSubject] = useState<LangValues>(emptyLangValues);
    const [ctaMessageTemplate, setCtaMessageTemplate] = useState<LangValues>(defaultMessageTemplate);

    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(editMode);

    // File input refs for pattern upload (one per color variant)
    const patternInputRefs = useRef<(HTMLInputElement | null)[]>([]);

    // Process image file to base64 with resize
    const processPatternImage = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                const img = new Image();
                img.onload = () => {
                    // Resize to 80x80 for swatch (displayed at 40x40, 2x for retina)
                    const canvas = document.createElement('canvas');
                    const size = 80;
                    canvas.width = size;
                    canvas.height = size;
                    const ctx = canvas.getContext('2d');
                    if (!ctx) {
                        reject(new Error('Canvas context not available'));
                        return;
                    }
                    // Draw image covering the square (center crop)
                    const scale = Math.max(size / img.width, size / img.height);
                    const x = (size - img.width * scale) / 2;
                    const y = (size - img.height * scale) / 2;
                    ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
                    // Convert to base64 (JPEG for smaller size)
                    const base64 = canvas.toDataURL('image/jpeg', 0.85);
                    resolve(base64);
                };
                img.onerror = () => reject(new Error('Image load failed'));
                img.src = e.target?.result as string;
            };
            reader.onerror = () => reject(new Error('File read failed'));
            reader.readAsDataURL(file);
        });
    };

    // Handle pattern file selection
    const handlePatternUpload = async (e: ChangeEvent<HTMLInputElement>, index: number) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            showNotification('Lütfen bir görsel dosyası seçin', 'error');
            return;
        }

        try {
            const base64 = await processPatternImage(file);
            updateColorVariant(index, 'pattern', base64);
            showNotification('Doku yüklendi', 'success');
        } catch (error) {
            showNotification('Görsel işlenemedi', 'error');
        }

        // Reset input
        e.target.value = '';
    };

    // Remove pattern from color variant
    const removePattern = (index: number) => {
        const newVariants = [...colorVariants];
        delete newVariants[index].pattern;
        setColorVariants(newVariants);
    };

    // Load edit data
    useEffect(() => {
        if (editMode && editId) {
            loadEditData(editId);
        }
    }, [editMode, editId]);

    async function loadEditData(id: string) {
        setLoading(true);
        try {
            const product = await getProduct(id);
            if (product) {
                setProductId(product.id);
                setName(toLangValues(product.name));
                setDescription(toLangValues(product.description));
                setBadge(toLangValues(product.badge));

                setColorVariants(product.colorVariants && product.colorVariants.length > 0
                    ? product.colorVariants.map(cv => ({
                        ...cv,
                        name: toLangValues(cv.name),
                        specs: toFormSpecs(cv.specs)
                    }))
                    : [emptyColorVariant()]);

                setCustomVariants((product.customVariants || []).map(cv => ({
                    ...cv,
                    name: toLangValues(cv.name),
                    specs: toFormSpecs(cv.specs)
                })));

                // Backward compat: eski customVariantsTitle'ı da kontrol et
                setSpecsTitle(toLangValues(product.specsTitle || (product as any).customVariantsTitle));
                setSpecs(toFormSpecs(product.specs));

                setImagesPath(product.images.basePath);
                setImagesCount(product.images.count);
                setImagesExt(product.images.extension);
                setWrapAround(product.wrapAround !== false); // default true
                setGalleryMode(product.galleryMode === true); // default false

                // Load variant overrides
                if (product.variantOverrides) {
                    const formOverrides: Record<string, FormVariantOverride> = {};
                    for (const [key, override] of Object.entries(product.variantOverrides)) {
                        formOverrides[key] = {
                            enabled: true,
                            count: override.count,
                            extension: override.extension,
                            wrapAround: override.wrapAround,
                            galleryMode: override.galleryMode
                        };
                    }
                    setVariantOverrides(formOverrides);
                }

                setFeatures((product.features || []).map(f => ({text: toLangValues(f.text)})));

                // Load CTA settings
                if (product.cta) {
                    setCtaEnabled(true);
                    setCtaLabel(toLangValues(product.cta.label));
                    setCtaAction(product.cta.action);
                    setCtaValue(product.cta.value);
                    setCtaEmailSubject(toLangValues(product.cta.emailSubject));
                    const template = toLangValues(product.cta.messageTemplate);
                    if (!template.tr) template.tr = defaultMessageTemplate().tr;
                    setCtaMessageTemplate(template);
                } else {
                    setCtaEnabled(false);
                }
            } else {
                showNotification('Ürün bulunamadı!', 'error');
                navigate('/products');
            }
        } catch (error) {
            showNotification('Ürün yüklenemedi!', 'error');
        }
        setLoading(false);
    }

    // Auto-generate product ID from Turkish name
    const handleNameChange = (values: LangValues) => {
        setName(values);
        if (!editMode && (!productId || productId === slugify(name.tr))) {
            setProductId(slugify(values.tr));
        }
    };

    // Drag state
    const [dragColorIndex, setDragColorIndex] = useState<number | null>(null);
    const [dragCustomIndex, setDragCustomIndex] = useState<number | null>(null);

    // Color variants handlers
    const addColorVariant = () => setColorVariants([...colorVariants, emptyColorVariant()]);
    const removeColorVariant = (index: number) => {
        setColorVariants(colorVariants.filter((_, i) => i !== index));
    };
    const updateColorVariant = (index: number, field: keyof FormColorVariant, value: string | boolean | LangValues) => {
        const newVariants = [...colorVariants];
        if (field === 'isDefault' && value === true) {
            newVariants.forEach((v, i) => {
                v.isDefault = i === index;
            });
        } else {
            newVariants[index] = {...newVariants[index], [field]: value};
        }
        setColorVariants(newVariants);
    };
    const reorderColorVariants = (fromIndex: number, toIndex: number) => {
        const newVariants = [...colorVariants];
        const [moved] = newVariants.splice(fromIndex, 1);
        newVariants.splice(toIndex, 0, moved);
        setColorVariants(newVariants);
    };

    // Color variant spec handlers
    const addColorVariantSpec = (variantIndex: number) => {
        const newVariants = [...colorVariants];
        const specs = newVariants[variantIndex].specs || [];
        newVariants[variantIndex].specs = [...specs, emptySpec()];
        setColorVariants(newVariants);
    };
    const removeColorVariantSpec = (variantIndex: number, specIndex: number) => {
        const newVariants = [...colorVariants];
        newVariants[variantIndex].specs = (newVariants[variantIndex].specs || []).filter((_, i) => i !== specIndex);
        setColorVariants(newVariants);
    };
    const updateColorVariantSpec = (variantIndex: number, specIndex: number, field: keyof FormVariantSpec, value: LangValues) => {
        const newVariants = [...colorVariants];
        const specs = [...(newVariants[variantIndex].specs || [])];
        specs[specIndex] = {...specs[specIndex], [field]: value};
        newVariants[variantIndex].specs = specs;
        setColorVariants(newVariants);
    };

    // Custom variants handlers
    const addCustomVariant = () => setCustomVariants([...customVariants, {id: '', name: emptyLangValues(), specs: []}]);
    const removeCustomVariant = (index: number) => setCustomVariants(customVariants.filter((_, i) => i !== index));
    const updateCustomVariant = (index: number, field: 'id' | 'name' | 'isDefault' | 'isDisabled', value: string | boolean | LangValues) => {
        const newVariants = [...customVariants];
        if (field === 'isDefault' && value === true) {
            newVariants.forEach((v, i) => {
                v.isDefault = i === index;
            });
        } else {
            newVariants[index] = {...newVariants[index], [field]: value};
        }
        setCustomVariants(newVariants);
    };
    const reorderCustomVariants = (fromIndex: number, toIndex: number) => {
        const newVariants = [...customVariants];
        const [moved] = newVariants.splice(fromIndex, 1);
        newVariants.splice(toIndex, 0, moved);
        setCustomVariants(newVariants);
    };

    // Size spec handlers
    const addVariantSpec = (variantIndex: number) => {
        const newVariants = [...customVariants];
        const specs = newVariants[variantIndex].specs || [];
        newVariants[variantIndex].specs = [...specs, emptySpec()];
        setCustomVariants(newVariants);
    };
    const removeVariantSpec = (variantIndex: number, specIndex: number) => {
        const newVariants = [...customVariants];
        newVariants[variantIndex].specs = (newVariants[variantIndex].specs || []).filter((_, i) => i !== specIndex);
        setCustomVariants(newVariants);
    };
    const updateVariantSpec = (variantIndex: number, specIndex: number, field: keyof FormVariantSpec, value: LangValues) => {
        const newVariants = [...customVariants];
        const specs = [...(newVariants[variantIndex].specs || [])];
        specs[specIndex] = {...specs[specIndex], [field]: value};
        newVariants[variantIndex].specs = specs;
        setCustomVariants(newVariants);
    };

    // Specs handlers (sabit özellikler)
    const addSpec = () => setSpecs([...specs, emptySpec()]);
    const removeSpec = (index: number) => setSpecs(specs.filter((_, i) => i !== index));
    const updateSpec = (index: number, field: keyof FormVariantSpec, value: LangValues) => {
        const newSpecs = [...specs];
        newSpecs[index] = {...newSpecs[index], [field]: value};
        setSpecs(newSpecs);
    };

    // Features handlers
    const addFeature = () => setFeatures([...features, {text: emptyLangValues()}]);
    const removeFeature = (index: number) => setFeatures(features.filter((_, i) => i !== index));
    const updateFeature = (index: number, value: LangValues) => {
        const newFeatures = [...features];
        newFeatures[index] = {text: value};
        setFeatures(newFeatures);
    };

    // Form submission
    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();

        const finalProductId = productId.trim();
        if (!finalProductId) {
            showNotification('Ürün ID gerekli!', 'error');
            return;
        }
        if (!/^[a-z0-9-]+$/.test(finalProductId)) {
            showNotification('Ürün ID sadece küçük harf, rakam ve tire içerebilir!', 'error');
            return;
        }
        if (!name.tr.trim()) {
            setEditLang('tr');
            showNotification('Ürün adı (Türkçe) gerekli!', 'error');
            return;
        }
        if (!imagesPath.trim()) {
            showNotification('Fotoğraf yolu gerekli!', 'error');
            return;
        }

        setSaving(true);

        try {
            if (!editMode) {
                const exists = await productExists(finalProductId);
                if (exists) {
                    showNotification('Bu ID zaten kullanılıyor!', 'error');
                    setSaving(false);
                    return;
                }
            }

            // Filter valid variants (remove empty ones, clean undefined fields)
            const validColorVariants = colorVariants
                .filter(v => v.id && v.name.tr.trim())
                .map(v => {
                    const cleaned: ColorVariant = {
                        id: v.id,
                        name: fromLangValues(v.name),
                        color: v.color
                    };
                    // Add pattern if exists
                    if (v.pattern) cleaned.pattern = v.pattern;
                    const validSpecs = fromFormSpecs(v.specs);
                    if (validSpecs.length > 0) cleaned.specs = validSpecs;
                    if (v.isDisabled) cleaned.isDisabled = true;
                    if (v.isDefault) cleaned.isDefault = true;
                    return cleaned;
                });

            const validCustomVariants = customVariants
                .filter(v => v.id && v.name.tr.trim())
                .map(v => {
                    const cleaned: CustomVariant = {
                        id: v.id,
                        name: fromLangValues(v.name)
                    };
                    const validSpecs = fromFormSpecs(v.specs);
                    if (validSpecs.length > 0) cleaned.specs = validSpecs;
                    if (v.isDisabled) cleaned.isDisabled = true;
                    if (v.isDefault) cleaned.isDefault = true;
                    return cleaned;
                });

            const validFeatures = features.filter(f => f.text.tr.trim()).map(f => ({
                text: fromLangValues(f.text)
            }));

            // Build CTA config if enabled (avoid undefined values for Firebase)
            let ctaConfig: CTAConfig | undefined;
            if (ctaEnabled && ctaLabel.tr.trim() && ctaValue.trim()) {
                const cta: CTAConfig = {
                    label: fromLangValues(ctaLabel),
                    action: ctaAction,
                    value: ctaValue.trim()
                };

                if (ctaAction === 'email' && ctaEmailSubject.tr.trim()) {
                    cta.emailSubject = fromLangValues(ctaEmailSubject);
                }
                if ((ctaAction === 'email' || ctaAction === 'whatsapp') && ctaMessageTemplate.tr.trim()) {
                    cta.messageTemplate = fromLangValues(ctaMessageTemplate);
                }

                ctaConfig = cta;
            }

            // Build config without undefined values (Firebase doesn't accept undefined)
            const config: ProductConfig = {
                name: fromLangValues(name),
                images: {
                    basePath: imagesPath.trim(),
                    count: typeof imagesCount === 'number' ? imagesCount : 24,
                    extension: imagesExt
                }
            };

            // Add wrapAround only if false (default is true)
            if (!wrapAround) config.wrapAround = false;

            // Add galleryMode only if true (default is false)
            if (galleryMode) config.galleryMode = true;

            // Add variant overrides (only enabled ones with actual values)
            const validOverrides: Record<string, VariantOverride> = {};
            for (const [key, override] of Object.entries(variantOverrides)) {
                if (override.enabled) {
                    const o: VariantOverride = {};
                    if (override.count !== undefined && override.count !== (typeof imagesCount === 'number' ? imagesCount : 24)) {
                        o.count = override.count;
                    }
                    if (override.extension && override.extension !== imagesExt) {
                        o.extension = override.extension;
                    }
                    if (override.wrapAround !== undefined && override.wrapAround !== wrapAround) {
                        o.wrapAround = override.wrapAround;
                    }
                    // galleryMode: only save if different from global setting
                    if (override.galleryMode !== undefined && override.galleryMode !== galleryMode) {
                        o.galleryMode = override.galleryMode;
                    }
                    // Only add if there are actual overrides
                    if (Object.keys(o).length > 0) {
                        validOverrides[key] = o;
                    }
                }
            }
            if (Object.keys(validOverrides).length > 0) {
                config.variantOverrides = validOverrides;
            }

            const validSpecs = fromFormSpecs(specs);

            // Add optional fields only if they have values
            if (description.tr.trim()) config.description = fromLangValues(description);
            if (badge.tr.trim()) config.badge = fromLangValues(badge);
            if (validColorVariants.length > 0) config.colorVariants = validColorVariants;
            if (validCustomVariants.length > 0) config.customVariants = validCustomVariants;
            if (specsTitle.tr.trim()) config.specsTitle = fromLangValues(specsTitle);
            if (validSpecs.length > 0) config.specs = validSpecs;
            if (validFeatures.length > 0) config.features = validFeatures;
            if (ctaConfig) config.cta = ctaConfig;

            // Edit modunda silinen alanları temizle
            if (editMode && editId) {
                const fieldsToDelete: string[] = [];
                if (!description.tr.trim()) fieldsToDelete.push('description');
                if (!badge.tr.trim()) fieldsToDelete.push('badge');
                if (validColorVariants.length === 0) fieldsToDelete.push('colorVariants');
                if (validCustomVariants.length === 0) fieldsToDelete.push('customVariants');
                if (!specsTitle.tr.trim()) fieldsToDelete.push('specsTitle', 'customVariantsTitle'); // eski alanı da sil
                if (validSpecs.length === 0) fieldsToDelete.push('specs');
                if (wrapAround) fieldsToDelete.push('wrapAround'); // default true ise sil
                if (!galleryMode) fieldsToDelete.push('galleryMode'); // default false ise sil
                if (Object.keys(validOverrides).length === 0) fieldsToDelete.push('variantOverrides');
                if (validFeatures.length === 0) fieldsToDelete.push('features');
                if (!ctaConfig) fieldsToDelete.push('cta');

                if (fieldsToDelete.length > 0) {
                    await deleteProductFields(editId, fieldsToDelete);
                }
            }

            if (editMode && editId) {
                await updateProduct(editId, config);
                showNotification('Ürün güncellendi!', 'success');
            } else {
                await createProduct(finalProductId, config);
                showNotification('Ürün oluşturuldu!', 'success');
                setTimeout(() => navigate('/products'), 1500);
            }
        } catch (error) {
            showNotification('Kaydetme hatası!', 'error');
        }

        setSaving(false);
    };

    const handleLogout = async () => {
        await signOut();
        navigate('/login');
    };

    const copyInstructions = async () => {
        const baseUrl = import.meta.env.VITE_IMAGE_BASE_URL;
        let text = `📁 FOTOĞRAF YÜKLEME TALİMATLARI\n${'═'.repeat(40)}\n\n`;

        // Döndürme modu bilgisi
        if (wrapAround) {
            text += `🔄 MOD: 360° Döndürme (Sonsuz döngü)\n`;
            text += `📌 Sıralama: 1. kare = başlangıç görüntüsü\n\n`;
        } else {
            text += `↔️ MOD: Sınırlı Döndürme\n`;
            text += `📌 Sıralama: Başlangıç karesini ORTAYA gelecek şekilde sıralayın\n`;
            text += `💡 Öneri: Tek sayı fotoğraf kullanın (örn: 17, 19, 21)\n\n`;
        }

        const count = typeof imagesCount === 'number' ? imagesCount : 24;
        const colors = colorVariants
            .filter(v => v.id && v.name.tr.trim())
            .map(v => ({id: v.id, name: v.name.tr}));

        // Eğer hiç renk yoksa default kullan
        const effectiveColors = colors.length > 0 ? colors : [{id: 'default', name: 'Standart'}];

        const filePrefix = imagesPath.split('/').pop() || imagesPath;
        effectiveColors.forEach(color => {
            const colorId = color.id;
            const override = variantOverrides[colorId];
            const hasOverride = override?.enabled;

            // Renk için geçerli değerler
            const colorCount = hasOverride && override.count !== undefined ? override.count : count;
            const colorExt = hasOverride && override.extension ? override.extension : imagesExt;
            const colorWrap = hasOverride && override.wrapAround !== undefined ? override.wrapAround : wrapAround;

            text += `📂 ${color.name}:\n`;
            text += `   Klasör: ${baseUrl}/${imagesPath}/${colorId}/\n`;
            text += `   Dosyalar: ${filePrefix}-1.${colorExt} ... ${filePrefix}-${colorCount}.${colorExt}\n`;

            // Override varsa özel bilgi ekle
            if (hasOverride) {
                const overrideInfo: string[] = [];
                if (override.count !== undefined && override.count !== count) {
                    overrideInfo.push(`${override.count} kare`);
                }
                if (override.extension && override.extension !== imagesExt) {
                    overrideInfo.push(`.${override.extension}`);
                }
                if (override.wrapAround !== undefined && override.wrapAround !== wrapAround) {
                    overrideInfo.push(override.wrapAround ? '360°' : 'Sınırlı');
                }
                if (overrideInfo.length > 0) {
                    text += `   ⚙️ Özel: ${overrideInfo.join(', ')}\n`;
                }
            }
            if (!colorWrap) {
                text += `   ↔️ Sınırlı döndürme - ortadan başlayacak şekilde sıralayın\n`;
            }
            text += '\n';
        });

        await navigator.clipboard.writeText(text);
        showNotification('Talimatlar kopyalandı!', 'success');
    };

    // Her dil için eksik çeviri sayısı (sadece kaydedilecek alanlar)
    const translatableFields: LangValues[] = [
        name, description, badge, specsTitle,
        ...specs.flatMap(s => [s.label, s.value]),
        ...[...colorVariants, ...customVariants]
            .filter(v => v.id)
            .flatMap(v => [v.name, ...(v.specs || []).flatMap(s => [s.label, s.value])]),
        ...features.map(f => f.text),
        ...(ctaEnabled ? [ctaLabel] : []),
        ...(ctaEnabled && ctaAction === 'email' ? [ctaEmailSubject] : []),
        ...(ctaEnabled && (ctaAction === 'email' || ctaAction === 'whatsapp') ? [ctaMessageTemplate] : []),
    ];
    const missingCounts = Object.fromEntries(LANGUAGES.map(lang => [
        lang,
        translatableFields.filter(values => isTranslationMissing(values, lang)).length
    ])) as Record<Language, number>;

    if (loading) {
        return (
            <div className="config-page">
                <div className="loading-state">
                    <div className="loader-spinner"></div>
                    <p>Yükleniyor...</p>
                </div>
                <style>{`
          .loading-state {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 100vh;
            color: #666;
          }
          .loader-spinner {
            width: 40px;
            height: 40px;
            border: 3px solid #e8e8e8;
            border-top-color: #e6a532;
            border-radius: 50%;
            animation: spin 0.8s linear infinite;
            margin-bottom: 16px;
          }
          @keyframes spin { to { transform: rotate(360deg); } }
        `}</style>
            </div>
        );
    }

    return (
        <div className="config-page">
            {/* Header */}
            <header className="config-header">
                <div className="config-header-inner">
                    <Link to="/" className="logo">
                        <img src="/logodeneme5-scaled.png" alt="Sibuzhome" className="logo-img"/>
                    </Link>
                    <h1 className="config-title">{editMode ? 'Ürün Düzenle' : 'Yeni Ürün Ekle'}</h1>
                    <div className="config-header-actions">
                        <Link to="/products" className="btn-secondary">
                            <ListIcon/>
                            <span>Ürünler</span>
                        </Link>
                        <button type="button" className="btn-secondary" onClick={handleLogout}>
                            <LogoutIcon/>
                            <span>Çıkış</span>
                        </button>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <div className="config-content">
                {/* Left: Form Panel */}
                <div className="config-form-panel">
                    <form className="config-form" onSubmit={handleSubmit}>
                        <LangTabs active={editLang} onChange={setEditLang} missingCounts={missingCounts}/>

                        {/* Basic Info */}
                        <section className="form-section">
                            <h2 className="form-section-title">Temel Bilgiler</h2>

                            <div className="form-group">
                                <label htmlFor="productId">Ürün ID (URL) *</label>
                                <input
                                    type="text"
                                    id="productId"
                                    value={productId}
                                    onChange={(e) => setProductId(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                                    required
                                    placeholder="Örn: looka-uyku-seti"
                                    disabled={editMode}
                                />
                                <span className="form-hint">
                  URL: /urun/{productId || 'ornek-urun'}
                </span>
                            </div>

                            <div className="form-group">
                                <label>Ürün Adı *</label>
                                <LangInput
                                    values={name}
                                    lang={editLang}
                                    onChange={handleNameChange}
                                    required
                                    placeholder="Örn: Looka Uyku Seti"
                                />
                            </div>

                            <div className="form-group">
                                <label>Açıklama</label>
                                <LangInput
                                    values={description}
                                    lang={editLang}
                                    onChange={setDescription}
                                    multiline
                                    rows={2}
                                    placeholder="Ürün açıklaması..."
                                />
                                <span className="form-hint">
                  Değişkenler: <code>{'{renk}'}</code> = Seçili renk, <code>{'{varyant}'}</code> = Seçili varyant
                </span>
                            </div>

                            <div className="form-group">
                                <label>Badge (Etiket)</label>
                                <LangInput
                                    values={badge}
                                    lang={editLang}
                                    onChange={setBadge}
                                    placeholder="Örn: Yeni Sezon"
                                />
                            </div>
                        </section>

                        {/* Specs Section */}
                        <section className="form-section">
                            <h2 className="form-section-title">Özellik Tablosu (Opsiyonel)</h2>
                            <p className="form-section-hint">Tüm varyantlar için geçerli sabit özellikler. Varyanta özgü
                                dinamik özellikler varsa tablonun altına eklenir.</p>

                            <div className="form-group">
                                <label>Tablo Başlığı</label>
                                <LangInput
                                    values={specsTitle}
                                    lang={editLang}
                                    onChange={setSpecsTitle}
                                    placeholder="örn: Ürün Özellikleri"
                                />
                            </div>

                            <div className="dynamic-list">
                                {specs.map((spec, index) => (
                                    <div key={index} className="dynamic-item spec-item-lang">
                                        <div className="spec-field-pair">
                                            <LangInput
                                                values={spec.label}
                                                lang={editLang}
                                                onChange={(v) => updateSpec(index, 'label', v)}
                                                placeholder="Özellik (örn: En)"
                                                className="input-label"
                                            />
                                            <LangInput
                                                values={spec.value}
                                                lang={editLang}
                                                onChange={(v) => updateSpec(index, 'value', v)}
                                                placeholder="Değer (örn: 150 cm)"
                                                className="input-value"
                                            />
                                        </div>
                                        <button type="button" className="btn-remove" onClick={() => removeSpec(index)}>
                                            <TrashIcon/>
                                        </button>
                                    </div>
                                ))}
                            </div>
                            <button type="button" className="btn-add" onClick={addSpec}>
                                <PlusIcon/>
                                <span>Özellik Ekle</span>
                            </button>
                        </section>

                        {/* Color Variants Section */}
                        <section className="form-section">
                            <h2 className="form-section-title">Renk Varyantları (Opsiyonel)</h2>
                            <p className="form-section-hint">Renk varyantı eklenmezse "default" kullanılır. Her renge
                                özgü dinamik özellikler ekleyebilirsiniz.</p>

                            <div className="dynamic-list">
                                {colorVariants.map((variant, index) => (
                                    <div
                                        key={index}
                                        className={`color-variant-card ${dragColorIndex === index ? 'dragging' : ''}`}
                                        draggable
                                        onDragStart={() => setDragColorIndex(index)}
                                        onDragOver={(e) => e.preventDefault()}
                                        onDrop={() => {
                                            if (dragColorIndex !== null && dragColorIndex !== index) {
                                                reorderColorVariants(dragColorIndex, index);
                                            }
                                            setDragColorIndex(null);
                                        }}
                                        onDragEnd={() => setDragColorIndex(null)}
                                    >
                                        <div className="color-variant-header">
                                            <span className="drag-handle" title="Sürükle">⋮⋮</span>
                                            <input
                                                type="text"
                                                value={variant.id}
                                                onChange={(e) => updateColorVariant(index, 'id', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                                                placeholder="ID"
                                                className="input-id"
                                            />
                                            <LangInput
                                                values={variant.name}
                                                lang={editLang}
                                                onChange={(v) => updateColorVariant(index, 'name', v)}
                                                placeholder="İsim (örn: Gri)"
                                                className="input-name"
                                                title="Renk adı"
                                            />
                                            <div className="color-pattern-group">
                                                <input
                                                    type="color"
                                                    value={variant.color}
                                                    onChange={(e) => updateColorVariant(index, 'color', e.target.value)}
                                                    className="input-color"
                                                    title="Renk seç"
                                                />
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    ref={(el) => {
                                                        patternInputRefs.current[index] = el;
                                                    }}
                                                    onChange={(e) => handlePatternUpload(e, index)}
                                                    style={{display: 'none'}}
                                                />
                                                {variant.pattern ? (
                                                    <div className="pattern-preview" title="Doku önizlemesi">
                                                        <img src={variant.pattern} alt="Doku"/>
                                                        <button
                                                            type="button"
                                                            className="pattern-remove"
                                                            onClick={() => removePattern(index)}
                                                            title="Dokuyu kaldır"
                                                        >
                                                            ×
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        className="btn-pattern-upload"
                                                        onClick={() => patternInputRefs.current[index]?.click()}
                                                        title="Doku yükle"
                                                    >
                                                        <ImageIcon/>
                                                    </button>
                                                )}
                                            </div>
                                            <label className="variant-toggle" title="Varsayılan">
                                                <input
                                                    type="checkbox"
                                                    checked={variant.isDefault || false}
                                                    onChange={(e) => updateColorVariant(index, 'isDefault', e.target.checked)}
                                                />
                                                <span>⭐</span>
                                            </label>
                                            <label className="variant-toggle" title="Stok Yok">
                                                <input
                                                    type="checkbox"
                                                    checked={variant.isDisabled || false}
                                                    onChange={(e) => updateColorVariant(index, 'isDisabled', e.target.checked)}
                                                />
                                                <span>🚫</span>
                                            </label>
                                            <button type="button" className="btn-remove"
                                                    onClick={() => removeColorVariant(index)}>
                                                <TrashIcon/>
                                            </button>
                                        </div>
                                        <div className="color-specs-list">
                                            {(variant.specs || []).map((spec, specIndex) => (
                                                <div key={specIndex} className="spec-item-lang">
                                                    <div className="spec-field-pair">
                                                        <LangInput
                                                            values={spec.label}
                                                            lang={editLang}
                                                            onChange={(v) => updateColorVariantSpec(index, specIndex, 'label', v)}
                                                            placeholder="Özellik"
                                                            className="input-label"
                                                        />
                                                        <LangInput
                                                            values={spec.value}
                                                            lang={editLang}
                                                            onChange={(v) => updateColorVariantSpec(index, specIndex, 'value', v)}
                                                            placeholder="Değer"
                                                            className="input-value"
                                                        />
                                                    </div>
                                                    <button type="button" className="btn-remove"
                                                            onClick={() => removeColorVariantSpec(index, specIndex)}>
                                                        <TrashIcon/>
                                                    </button>
                                                </div>
                                            ))}
                                            <button type="button" className="btn-add-spec"
                                                    onClick={() => addColorVariantSpec(index)}>
                                                <PlusIcon/>
                                                <span>Özellik Ekle</span>
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <button type="button" className="btn-add" onClick={addColorVariant}>
                                <PlusIcon/>
                                <span>Renk Ekle</span>
                            </button>
                        </section>

                        {/* Custom Variants Section */}
                        <section className="form-section">
                            <h2 className="form-section-title">Özel Varyantlar (Opsiyonel)</h2>
                            <p className="form-section-hint">Boyut, paket, materyal vb. varyantlar. Her varyanta özgü
                                dinamik özellikler ekleyebilirsiniz.</p>

                            <div className="dynamic-list">
                                {customVariants.map((variant, index) => (
                                    <div
                                        key={index}
                                        className={`size-variant-card ${dragCustomIndex === index ? 'dragging' : ''}`}
                                        draggable
                                        onDragStart={() => setDragCustomIndex(index)}
                                        onDragOver={(e) => e.preventDefault()}
                                        onDrop={() => {
                                            if (dragCustomIndex !== null && dragCustomIndex !== index) {
                                                reorderCustomVariants(dragCustomIndex, index);
                                            }
                                            setDragCustomIndex(null);
                                        }}
                                        onDragEnd={() => setDragCustomIndex(null)}
                                    >
                                        <div className="size-variant-header">
                                            <span className="drag-handle" title="Sürükle">⋮⋮</span>
                                            <input
                                                type="text"
                                                value={variant.id}
                                                onChange={(e) => updateCustomVariant(index, 'id', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                                                placeholder="ID"
                                                className="input-id"
                                            />
                                            <LangInput
                                                values={variant.name}
                                                lang={editLang}
                                                onChange={(v) => updateCustomVariant(index, 'name', v)}
                                                placeholder="İsim (örn: 160x200)"
                                                className="input-name"
                                                title="Varyant adı"
                                            />
                                            <label className="variant-toggle" title="Varsayılan">
                                                <input
                                                    type="checkbox"
                                                    checked={variant.isDefault || false}
                                                    onChange={(e) => updateCustomVariant(index, 'isDefault', e.target.checked)}
                                                />
                                                <span>⭐</span>
                                            </label>
                                            <label className="variant-toggle" title="Stok Yok">
                                                <input
                                                    type="checkbox"
                                                    checked={variant.isDisabled || false}
                                                    onChange={(e) => updateCustomVariant(index, 'isDisabled', e.target.checked)}
                                                />
                                                <span>🚫</span>
                                            </label>
                                            <button type="button" className="btn-remove"
                                                    onClick={() => removeCustomVariant(index)}>
                                                <TrashIcon/>
                                            </button>
                                        </div>
                                        <div className="size-specs-list">
                                            {(variant.specs || []).map((spec, specIndex) => (
                                                <div key={specIndex} className="spec-item-lang">
                                                    <div className="spec-field-pair">
                                                        <LangInput
                                                            values={spec.label}
                                                            lang={editLang}
                                                            onChange={(v) => updateVariantSpec(index, specIndex, 'label', v)}
                                                            placeholder="Özellik"
                                                            className="input-label"
                                                        />
                                                        <LangInput
                                                            values={spec.value}
                                                            lang={editLang}
                                                            onChange={(v) => updateVariantSpec(index, specIndex, 'value', v)}
                                                            placeholder="Değer"
                                                            className="input-value"
                                                        />
                                                    </div>
                                                    <button type="button" className="btn-remove"
                                                            onClick={() => removeVariantSpec(index, specIndex)}>
                                                        <TrashIcon/>
                                                    </button>
                                                </div>
                                            ))}
                                            <button type="button" className="btn-add-spec"
                                                    onClick={() => addVariantSpec(index)}>
                                                <PlusIcon/>
                                                <span>Özellik Ekle</span>
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <button type="button" className="btn-add" onClick={addCustomVariant}>
                                <PlusIcon/>
                                <span>Varyant Ekle</span>
                            </button>
                        </section>

                        {/* Images Section */}
                        <section className="form-section">
                            <h2 className="form-section-title">Fotoğraf Ayarları</h2>

                            <div className="form-group">
                                <label htmlFor="imagesPath">Fotoğraf Yolu *</label>
                                <input
                                    type="text"
                                    id="imagesPath"
                                    value={imagesPath}
                                    onChange={(e) => setImagesPath(e.target.value)}
                                    required
                                    placeholder="Örn: looka-uyku-seti"
                                />
                                <span className="form-hint">Fotoğrafların yükleneceği klasör adı</span>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="imagesCount">Fotoğraf Sayısı *</label>
                                    <input
                                        type="number"
                                        id="imagesCount"
                                        value={imagesCount}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            if (val === '') {
                                                setImagesCount('');
                                            } else {
                                                const num = parseInt(val);
                                                if (!isNaN(num)) setImagesCount(num);
                                            }
                                        }}
                                        onBlur={() => {
                                            if (imagesCount === '' || imagesCount < 1) setImagesCount(1);
                                            else if (imagesCount > 100) setImagesCount(100);
                                        }}
                                        required
                                        min={1}
                                        max={100}
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="imagesExt">Dosya Uzantısı</label>
                                    <select
                                        id="imagesExt"
                                        value={imagesExt}
                                        onChange={(e) => setImagesExt(e.target.value)}
                                    >
                                        <option value="jpg">JPG</option>
                                        <option value="png">PNG</option>
                                        <option value="webp">WebP</option>
                                    </select>
                                </div>
                            </div>

                            <div className="form-group">
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={galleryMode}
                                        onChange={(e) => {
                                            setGalleryMode(e.target.checked);
                                            // Galeri modunda wrapAround mantıksız
                                            if (e.target.checked) setWrapAround(false);
                                        }}
                                    />
                                    <span>📷 Galeri Modu (Statik fotoğraflar)</span>
                                </label>
                                {galleryMode && (
                                    <div className="rotation-hint hint-gallery">
                                        <strong>Galeri Modu:</strong> Fotoğraflar arası ok tuşları, swipe veya thumbnail
                                        ile geçiş yapılır.
                                        <br/>
                                        <em>360° döndürme yerine klasik fotoğraf galerisi deneyimi.</em>
                                    </div>
                                )}
                            </div>

                            {!galleryMode && (
                                <div className="form-group">
                                    <label className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={wrapAround}
                                            onChange={(e) => setWrapAround(e.target.checked)}
                                        />
                                        <span>360° Döndürme (Sonsuz döngü)</span>
                                    </label>
                                    <div className={`rotation-hint ${wrapAround ? 'hint-360' : 'hint-limited'}`}>
                                        {wrapAround ? (
                                            <>
                                                <strong>360° Mod:</strong> Ürün sürekli döner, başa sarar.
                                                <br/>
                                                <em>Fotoğraf sıralaması: 1. kare = başlangıç görüntüsü</em>
                                            </>
                                        ) : (
                                            <>
                                                <strong>Sınırlı Mod:</strong> Ürün belirli açıda döner, sınırda durur.
                                                <br/>
                                                <em>Fotoğraf sıralaması: Başlangıç karesini ORTAYA gelecek şekilde
                                                    sıralayın. Tek sayı fotoğraf önerilir.</em>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}
                        </section>

                        {/* Variant Overrides Section - sadece varyantlar varsa göster */}
                        {(() => {
                            const validColors = colorVariants
                                .filter(v => v.id && v.name.tr.trim())
                                .map(v => ({id: v.id, name: v.name.tr}));
                            const effectiveColors = validColors.length > 0 ? validColors : [{
                                id: 'default',
                                name: 'Standart'
                            }];

                            // Sadece birden fazla renk varsa göster
                            if (effectiveColors.length <= 1) return null;

                            return (
                                <section className="form-section">
                                    <h2 className="form-section-title">Renk Ayarları</h2>
                                    <p className="form-section-desc">Farklı ayar gereken renkler için override
                                        tanımlayın. Boş bırakılanlar yukarıdaki varsayılan ayarları kullanır.</p>

                                    <div className="override-list">
                                        {effectiveColors.map(color => {
                                            const colorId = color.id;
                                            const override = variantOverrides[colorId] || {enabled: false};

                                            const updateOverride = (updates: Partial<FormVariantOverride>) => {
                                                setVariantOverrides(prev => ({
                                                    ...prev,
                                                    [colorId]: {...prev[colorId], ...updates}
                                                }));
                                            };

                                            return (
                                                <div key={colorId}
                                                     className={`override-item ${override.enabled ? 'override-active' : ''}`}>
                                                    <label className="checkbox-label override-header">
                                                        <input
                                                            type="checkbox"
                                                            checked={override.enabled || false}
                                                            onChange={(e) => updateOverride({
                                                                enabled: e.target.checked,
                                                                count: e.target.checked ? (typeof imagesCount === 'number' ? imagesCount : 24) : undefined,
                                                                extension: e.target.checked ? imagesExt : undefined,
                                                                wrapAround: e.target.checked ? wrapAround : undefined,
                                                                galleryMode: e.target.checked ? galleryMode : undefined
                                                            })}
                                                        />
                                                        <span
                                                            className="override-name">{color.name}</span>
                                                        {override.enabled &&
                                                            <span className="override-badge">Özel Ayar</span>}
                                                    </label>

                                                    {override.enabled && (
                                                        <div className="override-fields">
                                                            <div className="override-field">
                                                                <label>Fotoğraf Sayısı</label>
                                                                <input
                                                                    type="number"
                                                                    value={override.count ?? ''}
                                                                    onChange={(e) => {
                                                                        const val = e.target.value;
                                                                        if (val === '') {
                                                                            updateOverride({count: undefined});
                                                                        } else {
                                                                            const num = parseInt(val);
                                                                            if (!isNaN(num) && num > 0 && num <= 100) {
                                                                                updateOverride({count: num});
                                                                            }
                                                                        }
                                                                    }}
                                                                    min={1}
                                                                    max={100}
                                                                    placeholder={String(typeof imagesCount === 'number' ? imagesCount : 24)}
                                                                />
                                                            </div>
                                                            <div className="override-field">
                                                                <label>Uzantı</label>
                                                                <select
                                                                    value={override.extension || imagesExt}
                                                                    onChange={(e) => updateOverride({extension: e.target.value})}
                                                                >
                                                                    <option value="jpg">JPG</option>
                                                                    <option value="png">PNG</option>
                                                                    <option value="webp">WebP</option>
                                                                </select>
                                                            </div>
                                                            <div className="override-field override-field-checkbox">
                                                                <label className="checkbox-label">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={override.galleryMode ?? galleryMode}
                                                                        onChange={(e) => updateOverride({
                                                                            galleryMode: e.target.checked,
                                                                            // Galeri modunda 360° döndürme mantıksız, kapatıyoruz
                                                                            wrapAround: e.target.checked ? false : (override.wrapAround ?? wrapAround)
                                                                        })}
                                                                    />
                                                                    <span>📷 Galeri Modu</span>
                                                                </label>
                                                            </div>
                                                            {!(override.galleryMode ?? galleryMode) && (
                                                                <div
                                                                    className="override-field override-field-checkbox">
                                                                    <label className="checkbox-label">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={override.wrapAround ?? wrapAround}
                                                                            onChange={(e) => updateOverride({wrapAround: e.target.checked})}
                                                                        />
                                                                        <span>360° Döndürme</span>
                                                                    </label>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </section>
                            );
                        })()}

                        {/* CTA Button Section */}
                        <section className="form-section">
                            <h2 className="form-section-title">Aksiyon Butonu</h2>

                            <div className="form-group">
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={ctaEnabled}
                                        onChange={(e) => setCtaEnabled(e.target.checked)}
                                    />
                                    <span>Aksiyon butonu göster</span>
                                </label>
                            </div>

                            {ctaEnabled && (
                                <>
                                    <div className="form-group">
                                        <label>Buton Yazısı</label>
                                        <LangInput
                                            values={ctaLabel}
                                            lang={editLang}
                                            onChange={setCtaLabel}
                                            placeholder="Örn: Teklif Al"
                                        />
                                    </div>
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="ctaAction">İşlem Türü</label>
                                            <select
                                                id="ctaAction"
                                                value={ctaAction}
                                                onChange={(e) => setCtaAction(e.target.value as CTAActionType)}
                                            >
                                                {CTA_ACTIONS.map(action => (
                                                    <option key={action.id} value={action.id}>{action.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="ctaValue">
                                            {ctaAction === 'email' ? 'E-posta Adresi' :
                                                ctaAction === 'whatsapp' ? 'Telefon Numarası' : 'URL'}
                                        </label>
                                        <input
                                            type="text"
                                            id="ctaValue"
                                            value={ctaValue}
                                            onChange={(e) => setCtaValue(e.target.value)}
                                            placeholder={CTA_ACTIONS.find(a => a.id === ctaAction)?.placeholder}
                                        />
                                    </div>

                                    {ctaAction === 'email' && (
                                        <div className="form-group">
                                            <label>E-posta Konusu</label>
                                            <LangInput
                                                values={ctaEmailSubject}
                                                lang={editLang}
                                                onChange={setCtaEmailSubject}
                                                placeholder="Örn: {urun} Hakkında Bilgi Talebi"
                                            />
                                            <span
                                                className="form-hint">Değişkenler: {'{urun}'}, {'{renk}'}, {'{link}'}</span>
                                        </div>
                                    )}

                                    {(ctaAction === 'email' || ctaAction === 'whatsapp') && (
                                        <div className="form-group">
                                            <label>Mesaj Şablonu</label>
                                            <LangInput
                                                values={ctaMessageTemplate}
                                                lang={editLang}
                                                onChange={setCtaMessageTemplate}
                                                multiline
                                                rows={5}
                                                placeholder="Mesaj içeriği..."
                                            />
                                            <span className="form-hint">
                        Değişkenler: <code>{'{urun}'}</code>, <code>{'{renk}'}</code>, <code>{'{varyant}'}</code>, <code>{'{link}'}</code>
                      </span>
                                        </div>
                                    )}
                                </>
                            )}
                        </section>

                        {/* Features Section */}
                        <section className="form-section">
                            <div className="form-section-header">
                                <h2 className="form-section-title">Özellik Kartları (Opsiyonel)</h2>
                                <a
                                    href="https://www.textsymbols.net/p/symbols.html"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn-icon-select"
                                >
                                    🔣 Icon Seç
                                </a>
                            </div>
                            <p className="form-section-hint">Başına emoji/sembol ekleyebilirsiniz. Örn: "✓ 2 Yıl
                                Garanti" veya "🚚 Ücretsiz Kargo"</p>

                            <div className="dynamic-list">
                                {features.map((feature, index) => (
                                    <div key={index} className="dynamic-item feature-item-lang">
                                        <LangInput
                                            values={feature.text}
                                            lang={editLang}
                                            onChange={(v) => updateFeature(index, v)}
                                            placeholder="Örn: ✓ 2 Yıl Garanti"
                                            className="feature-input"
                                        />
                                        <button type="button" className="btn-remove"
                                                onClick={() => removeFeature(index)}>
                                            <TrashIcon/>
                                        </button>
                                    </div>
                                ))}
                            </div>
                            <button type="button" className="btn-add" onClick={addFeature}>
                                <PlusIcon/>
                                <span>Özellik Ekle</span>
                            </button>
                        </section>

                        {/* Actions */}
                        <section className="form-section form-actions form-actions-inline">
                            <button type="submit" className="btn-primary" disabled={saving}>
                                <SaveIcon/>
                                <span>{saving ? 'Kaydediliyor...' : editMode ? 'Güncelle' : 'Kaydet'}</span>
                            </button>
                            <Link to="/products" className="btn-secondary">
                                İptal
                            </Link>
                        </section>
                    </form>
                </div>

                {/* Right: Sidebar */}
                <div className="config-output-panel">
                    {/* Product URL Preview */}
                    {productId && (
                        <div className="sidebar-card">
                            <h3 className="sidebar-title">🔗 Ürün URL</h3>
                            <code className="url-preview">/urun/{productId}</code>
                            {editMode && (
                                <a
                                    href={`/urun/${productId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn-preview"
                                >
                                    Önizle →
                                </a>
                            )}
                        </div>
                    )}

                    {/* Image Instructions */}
                    <div className="sidebar-card sidebar-card-dark">
                        <h3 className="sidebar-title-light">
                            <FolderIcon/>
                            Fotoğraf Talimatları
                        </h3>
                        <pre className="instructions-content">
              {(() => {
                  const baseUrl = import.meta.env.VITE_IMAGE_BASE_URL;
                  if (!imagesPath) {
                      return 'Fotoğraf yolu girin...';
                  }
                  const colors = colorVariants
                      .filter(v => v.id && v.name.tr.trim())
                      .map(v => ({id: v.id, name: v.name.tr}));
                  const effectiveColors = colors.length > 0 ? colors : [{id: 'default', name: 'Standart'}];
                  const filePrefix = imagesPath.split('/').pop() || imagesPath;
                  const defaultCount = typeof imagesCount === 'number' ? imagesCount : 24;
                  let text = '';
                  effectiveColors.forEach(color => {
                      const override = variantOverrides[color.id];
                      const hasOverride = override?.enabled;
                      const colorCount = hasOverride && override.count !== undefined ? override.count : defaultCount;
                      const colorExt = hasOverride && override.extension ? override.extension : imagesExt;
                      text += `📂 ${color.name}:\n`;
                      text += `${baseUrl}/${imagesPath}/${color.id}/\n`;
                      text += `${filePrefix}-1.${colorExt} ... ${filePrefix}-${colorCount}.${colorExt}\n\n`;
                  });
                  return text;
              })()}
            </pre>
                        <button type="button" className="btn-copy" onClick={copyInstructions}>
                            <CopyIcon/>
                            Kopyala
                        </button>
                    </div>

                    {/* Quick Info */}
                    <div className="sidebar-card">
                        <h3 className="sidebar-title">📊 Özet</h3>
                        <div className="quick-stats">
                            <div className="stat-item">
                                <span className="stat-value">{colorVariants.filter(v => v.id).length || 1}</span>
                                <span className="stat-label">Renk</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-value">{customVariants.filter(v => v.id).length || 1}</span>
                                <span className="stat-label">Boyut</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-value">{imagesCount || 24}</span>
                                <span className="stat-label">Fotoğraf</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {NotificationComponent}

            <style>{`
        /* Checkbox Label */
        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 10px;
          cursor: pointer;
          font-size: 0.95rem;
          color: #374151;
        }

        .checkbox-label input[type="checkbox"] {
          width: 20px;
          height: 20px;
          accent-color: #c9a962;
          cursor: pointer;
        }

        .form-hint code {
          background: #e5e7eb;
          padding: 2px 6px;
          border-radius: 4px;
          font-family: 'Monaco', 'Menlo', monospace;
          font-size: 0.8rem;
          color: #1a1a1a;
        }

        /* Rotation Hint */
        .rotation-hint {
          margin-top: 8px;
          padding: 12px;
          border-radius: 8px;
          font-size: 0.85rem;
          line-height: 1.5;
        }
        .rotation-hint strong {
          display: block;
          margin-bottom: 4px;
        }
        .rotation-hint em {
          color: #6b7280;
          font-style: normal;
        }
        .hint-360 {
          background: #ecfdf5;
          border: 1px solid #a7f3d0;
          color: #065f46;
        }
        .hint-limited {
          background: #fef3c7;
          border: 1px solid #fcd34d;
          color: #92400e;
        }
        .hint-gallery {
          background: #ede9fe;
          border: 1px solid #c4b5fd;
          color: #5b21b6;
        }

        /* Override Section */
        .form-section-desc {
          color: #6b7280;
          font-size: 0.9rem;
          margin-bottom: 16px;
        }
        .override-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .override-item {
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 10px;
          padding: 12px;
          transition: all 0.2s;
        }
        .override-item.override-active {
          background: #fefce8;
          border-color: #fcd34d;
        }
        .override-header {
          display: flex;
          align-items: center;
          gap: 8px;
          width: 100%;
        }
        .override-name {
          flex: 1;
          font-weight: 500;
          color: #374151;
        }
        .override-badge {
          font-size: 0.75rem;
          padding: 2px 8px;
          background: #fbbf24;
          color: #78350f;
          border-radius: 4px;
          font-weight: 500;
        }
        .override-fields {
          display: flex;
          gap: 12px;
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px solid #e5e7eb;
          flex-wrap: wrap;
        }
        .override-field {
          display: flex;
          flex-direction: column;
          gap: 4px;
          min-width: 100px;
        }
        .override-field label {
          font-size: 0.8rem;
          color: #6b7280;
          font-weight: 500;
        }
        .override-field input,
        .override-field select {
          padding: 6px 10px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 0.9rem;
          width: 100%;
        }
        .override-field input[type="number"] {
          width: 80px;
        }
        .override-field-checkbox {
          display: flex;
          align-items: flex-end;
          padding-bottom: 4px;
        }
        .override-field-checkbox .checkbox-label {
          font-size: 0.85rem;
        }

        /* Variant Item */
        .variant-item .dynamic-item-fields {
          flex-wrap: wrap;
        }

        .input-sm { flex: 0 0 80px; }
        .input-md { flex: 1; min-width: 100px; }
        .input-num { flex: 0 0 80px; }
        .input-color { flex: 0 0 40px; height: 38px; padding: 4px; }

        /* Color + Pattern Group */
        .color-pattern-group {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .btn-pattern-upload {
          width: 38px;
          height: 38px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f3f4f6;
          border: 1.5px dashed #d1d5db;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.15s ease;
        }

        .btn-pattern-upload:hover {
          background: #e5e7eb;
          border-color: #9ca3af;
        }

        .btn-pattern-upload svg {
          width: 18px;
          height: 18px;
          color: #6b7280;
        }

        .pattern-preview {
          position: relative;
          width: 38px;
          height: 38px;
          border-radius: 6px;
          overflow: hidden;
          border: 1.5px solid #d1d5db;
        }

        .pattern-preview img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .pattern-remove {
          position: absolute;
          top: -4px;
          right: -4px;
          width: 18px;
          height: 18px;
          background: #ef4444;
          color: white;
          border: none;
          border-radius: 50%;
          font-size: 14px;
          line-height: 1;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.15s ease;
        }

        .pattern-preview:hover .pattern-remove {
          opacity: 1;
        }

        /* Color Variant Card */
        .color-variant-card {
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          overflow: hidden;
        }

        .color-variant-header {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px;
          background: #f3f4f6;
          border-bottom: 1px solid #e5e7eb;
        }

        .color-variant-header .input-id {
          width: 80px;
          flex-shrink: 0;
        }

        .color-variant-header .input-name {
          flex: 1;
        }

        .color-variant-header input:not([type="color"]):not([type="checkbox"]) {
          padding: 8px 12px;
          border: 1.5px solid #e5e7eb;
          border-radius: 6px;
          font-size: 0.9rem;
        }

        .color-specs-list {
          padding: 12px;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        /* Varyant içi spec satırı - yatay düzen */
        .color-specs-list .spec-item-lang,
        .size-specs-list .spec-item-lang {
          display: grid;
          grid-template-columns: 1fr auto;
          gap: 12px;
          align-items: center;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 12px;
          margin-bottom: 0;
        }

        .color-specs-list .spec-field-pair,
        .size-specs-list .spec-field-pair {
          display: flex;
          align-items: center;
          gap: 8px;
          min-width: 0;
        }

        .color-specs-list .spec-field-pair input,
        .size-specs-list .spec-field-pair input {
          flex: 1;
          min-width: 0;
          padding: 8px 10px;
          border: 1.5px solid #e5e7eb;
          border-radius: 6px;
          font-size: 0.85rem;
        }

        .color-specs-list .spec-field-pair input:focus,
        .size-specs-list .spec-field-pair input:focus {
          border-color: #e6a532;
          outline: none;
        }

        .spec-fields {
          display: flex;
          gap: 12px;
          flex: 1;
        }
        .spec-fields .input-label { flex: 0 0 140px; }
        .spec-fields .input-value { flex: 1; }

        .size-variant-card {
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          overflow: hidden;
        }

        .size-variant-header {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px;
          background: #f3f4f6;
          border-bottom: 1px solid #e5e7eb;
        }

        .size-variant-header .input-id {
          width: 80px;
          flex-shrink: 0;
        }

        .size-variant-header .input-name {
          flex: 1;
        }

        .size-variant-header input {
          padding: 8px 12px;
          border: 1.5px solid #e5e7eb;
          border-radius: 6px;
          font-size: 0.9rem;
        }

        .size-specs-list {
          padding: 12px;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .specs-title-input {
          width: 100%;
          padding: 10px 12px;
          border: 1.5px solid #e5e7eb;
          border-radius: 6px;
          font-size: 0.9rem;
          font-weight: 500;
          background: white;
          margin-bottom: 4px;
        }

        .specs-title-input:focus {
          border-color: #e6a532;
          outline: none;
        }

        .size-spec-row {
          display: flex;
          gap: 8px;
          align-items: center;
        }

        .size-spec-row input {
          flex: 1;
          padding: 8px 12px;
          border: 1.5px solid #e5e7eb;
          border-radius: 6px;
          font-size: 0.85rem;
          background: white;
        }

        .btn-remove-sm {
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: transparent;
          border: none;
          color: #9ca3af;
          cursor: pointer;
          border-radius: 6px;
          flex-shrink: 0;
        }

        .btn-remove-sm:hover {
          background: #fee2e2;
          color: #dc2626;
        }

        .btn-remove-sm svg {
          width: 14px;
          height: 14px;
        }

        .btn-add-spec {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 8px;
          background: transparent;
          border: 1.5px dashed #d1d5db;
          border-radius: 6px;
          font-size: 0.8rem;
          color: #9ca3af;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-add-spec:hover {
          border-color: #e6a532;
          color: #e6a532;
          background: rgba(230, 165, 50, 0.05);
        }

        .btn-add-spec svg {
          width: 14px;
          height: 14px;
        }

        .variant-toggle {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          background: #f3f4f6;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          cursor: pointer;
          font-size: 1rem;
          transition: all 0.2s;
          flex-shrink: 0;
        }

        .variant-toggle:hover {
          background: #e5e7eb;
        }

        .variant-toggle input {
          display: none;
        }

        .variant-toggle:has(input:checked) {
          background: #fef3c7;
          border-color: #f59e0b;
        }

        .variant-toggle:has(input:checked) span {
          transform: scale(1.1);
        }

        /* Sidebar Cards */
        .sidebar-card {
          background: white;
          border-radius: 12px;
          padding: 20px;
          margin-bottom: 16px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.04);
          border: 1px solid #e5e7eb;
        }

        .sidebar-card-dark {
          background: #1a1a1a;
          border: none;
        }

        .sidebar-title {
          font-size: 0.9rem;
          font-weight: 600;
          color: #1a1a1a;
          margin: 0 0 12px 0;
        }

        .sidebar-title-light {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 0.9rem;
          font-weight: 600;
          color: white;
          margin: 0 0 12px 0;
        }

        .sidebar-title-light svg {
          width: 16px;
          height: 16px;
          color: #c9a962;
        }

        .url-preview {
          display: block;
          background: #f3f4f6;
          padding: 10px 14px;
          border-radius: 8px;
          font-size: 0.85rem;
          color: #374151;
          margin-bottom: 12px;
          word-break: break-all;
        }

        .btn-preview {
          display: block;
          text-align: center;
          padding: 10px;
          background: #c9a962;
          color: #1a1a1a;
          text-decoration: none;
          border-radius: 8px;
          font-size: 0.85rem;
          font-weight: 600;
          transition: background 0.2s;
        }

        .btn-preview:hover {
          background: #d4b896;
        }

        .instructions-content {
          background: #2d2d2d;
          padding: 14px;
          border-radius: 8px;
          margin: 0 0 12px 0;
          font-size: 0.8rem;
          font-family: 'Monaco', 'Menlo', monospace;
          white-space: pre-wrap;
          color: #a3e635;
          max-height: 180px;
          overflow-y: auto;
          line-height: 1.5;
        }

        .btn-copy {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          width: 100%;
          padding: 10px;
          background: #c9a962;
          color: #1a1a1a;
          border: none;
          border-radius: 8px;
          font-size: 0.85rem;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.2s;
        }

        .btn-copy:hover {
          background: #d4b896;
        }

        .btn-copy svg {
          width: 14px;
          height: 14px;
        }

        .quick-stats {
          display: flex;
          gap: 16px;
        }

        .stat-item {
          flex: 1;
          text-align: center;
          padding: 12px 8px;
          background: #f9fafb;
          border-radius: 8px;
        }

        .stat-value {
          display: block;
          font-size: 1.5rem;
          font-weight: 700;
          color: #1a1a1a;
        }

        .stat-label {
          display: block;
          font-size: 0.75rem;
          color: #6b7280;
          margin-top: 2px;
        }

        @media (max-width: 1200px) {
          .config-output-panel {
            position: static !important;
          }
        }
      `}</style>
        </div>
    );
}
